import { useState, useCallback } from "react";
import { editTemplate } from "@/features/templates/api/templatesApi";

export interface TemplateChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface UseTemplateChatProps {
  jobId: string | null;
  onTemplateUpdate: (template: string, examples: string[]) => void;
  showError: (message: string) => void;
}

export const useTemplateChat = ({
  jobId,
  onTemplateUpdate,
  showError,
}: UseTemplateChatProps) => {
  const [messages, setMessages] = useState<TemplateChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading || !jobId) return;

      const userMessage: TemplateChatMessage = { role: "user", content: text };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const {
          template: newTemplate,
          generatedExamples: newExamples,
          summary,
        } = await editTemplate({ jobId, message: text });
        onTemplateUpdate(newTemplate, newExamples);

        const assistantMessage: TemplateChatMessage = {
          role: "assistant",
          content: summary,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error ?? "Failed to edit template";
        showError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [jobId, isLoading, onTemplateUpdate, showError]
  );

  const resetChat = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, isLoading, sendMessage, resetChat };
};
